import axios from 'axios';

const BOT_API_URL = process.env.VUE_APP_TEAMIO_BOT_API_URL || 'https://ilzyr90kz6.execute-api.us-east-1.amazonaws.com/dev';

// AutoRecall is real-only since 2026-06-18. Synthetic simulation (and the local agent-poc
// server it required) was removed — testing is a real ElevenLabs call to a tester-picked
// number (see triggerAutorecallTestCall). Real calls return immediately with a callSid;
// the outcome is recorded asynchronously by the ElevenLabs post-call webhook.

/**
 * Trigger a real AutoRecall call to a patient. Returns immediately with callSid.
 * @param {Object} params - { siteID, patNum, taskID, callReason }
 */
export async function triggerAutorecallRealCall({ siteID, patNum, taskID, callReason }) {
  const body = { siteID, patNum, taskID, callReason };
  const response = await axios.post(`${BOT_API_URL}/trigger-autorecall-call`, body, { timeout: 10000 });
  return response.data;
}

/**
 * Place a TEST call: a real ElevenLabs call to a tester-picked number (`toNumber`),
 * voiced with `patNum`'s script context. Never dials the patient and never touches a
 * real follow-up task (logged server-side as mode='test').
 * @param {Object} params - { siteID, patNum, toNumber, callReason }
 */
export async function triggerAutorecallTestCall({ siteID, patNum, toNumber, callReason = 'recall' }) {
  const body = { siteID, patNum, toNumber, callReason };
  const response = await axios.post(`${BOT_API_URL}/trigger-autorecall-call`, body, { timeout: 10000 });
  return response.data;
}

/**
 * Backward-compatible entry point used by the Patient Profile / Overdue list / CreateFollowup
 * AI Call buttons. Always a real call now (no simulation).
 * @param {Object} params - { siteID, patNum, taskID, callReason }
 */
export async function triggerAutorecallCall(params) {
  return triggerAutorecallRealCall(params);
}

// All three batch operations (start/abort/status) go through one endpoint,
// dispatched on `action`, because the shared API Gateway is at its 300-resource
// soft limit. Once the AWS limit-increase request is granted we can split
// back into REST-pure routes.

/**
 * Kick off a batch AutoRecall run. Lambda handles the whole run synchronously
 * (up to 50 patients, ~15 min Lambda ceiling) and returns totals + events when done.
 * For live progress during the run, poll `pollAutorecallBatchStatus(batchRunId)`.
 *
 * @param {Object} params
 * @param {string} params.siteID
 * @param {Array<{patNum:number, taskID?:string, callReason?:string}>} params.patients
 * @param {'recall'|'treatment'} [params.callReason='recall']
 * @param {string} [params.initiatedBy]
 * @param {string} [params.listType]
 * @returns {Promise<{success, batchRunId, aborted, completed, totals, events}>}
 */
export async function triggerAutorecallBatch({ siteID, patients, callReason, initiatedBy, listType }) {
  const body = { action: 'start', siteID, patients };
  if (callReason) body.callReason = callReason;
  if (initiatedBy) body.initiatedBy = initiatedBy;
  if (listType) body.listType = listType;
  const response = await axios.post(`${BOT_API_URL}/autorecall-batch`, body, { timeout: 16 * 60 * 1000 });
  return response.data;
}

/** Abort a running batch. Idempotent. */
export async function abortAutorecallBatch(batchRunId) {
  const response = await axios.post(`${BOT_API_URL}/autorecall-batch`,
    { action: 'abort', batchRunId }, { timeout: 10000 });
  return response.data;
}

/**
 * Snapshot the batch's current state + all its calls so far.
 * @returns {Promise<{success, batchRun, calls}>}
 */
export async function pollAutorecallBatchStatus(batchRunId) {
  const response = await axios.post(`${BOT_API_URL}/autorecall-batch`,
    { action: 'status', batchRunId }, { timeout: 10000 });
  return response.data;
}
